import type { TourExperienceStop, TourTripLocation } from '@/lib/types/tours.types'

export interface TourMapGeometry {
  center: [number, number]
  bounds: { ne: [number, number]; sw: [number, number] }
  line: GeoJSON.Feature<GeoJSON.LineString> | null
  stops: GeoJSON.FeatureCollection<GeoJSON.Point>
}

export function buildTourMapGeometry(
  stops: TourExperienceStop[],
  latestLocation: TourTripLocation | null,
  routeGeojson?: unknown,
): TourMapGeometry | null {
  const validStops = stops.filter(
    (stop) =>
      Number.isFinite(stop.latitude) &&
      Number.isFinite(stop.longitude) &&
      Math.abs(stop.latitude) <= 90 &&
      Math.abs(stop.longitude) <= 180,
  )
  const stopCoordinates: [number, number][] = validStops.map((stop) => [
    stop.longitude,
    stop.latitude,
  ])
  const routeCoordinates = readRouteCoordinates(routeGeojson)
  const lineCoordinates: [number, number][] =
    routeCoordinates.length >= 2 ? [...routeCoordinates] : []
  if (lineCoordinates.length === 0 && stopCoordinates.length === 0 && latestLocation) {
    lineCoordinates.push([latestLocation.longitude, latestLocation.latitude])
  }
  if (lineCoordinates.length === 0 && stopCoordinates.length === 0 && !latestLocation) {
    return null
  }

  const all = latestLocation
    ? [
        ...lineCoordinates,
        ...stopCoordinates,
        [latestLocation.longitude, latestLocation.latitude] as [number, number],
      ]
    : [...lineCoordinates, ...stopCoordinates]
  if (all.length === 0) return null
  const lngs = all.map(([lng]) => lng)
  const lats = all.map(([, lat]) => lat)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const padding = 0.015

  return {
    center: latestLocation
      ? [latestLocation.longitude, latestLocation.latitude]
      : (lineCoordinates[Math.floor((lineCoordinates.length - 1) / 2)] ??
        stopCoordinates[Math.floor((stopCoordinates.length - 1) / 2)]!),
    bounds: {
      ne: [maxLng + padding, maxLat + padding],
      sw: [minLng - padding, minLat - padding],
    },
    line:
      lineCoordinates.length >= 2
        ? {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: lineCoordinates,
            },
          }
        : null,
    stops: {
      type: 'FeatureCollection',
      features: validStops.map((stop) => ({
        type: 'Feature',
        id: stop.id,
        properties: { position: stop.position, kind: stop.kind },
        geometry: { type: 'Point', coordinates: [stop.longitude, stop.latitude] },
      })),
    },
  }
}

export function readRouteCoordinates(value: unknown): [number, number][] {
  if (!value || typeof value !== 'object') return []
  const candidate = value as {
    type?: unknown
    geometry?: unknown
    coordinates?: unknown
    features?: unknown
  }
  if (candidate.type === 'Feature') return readRouteCoordinates(candidate.geometry)
  if (candidate.type === 'FeatureCollection' && Array.isArray(candidate.features)) {
    for (const feature of candidate.features) {
      const coordinates = readRouteCoordinates(feature)
      if (coordinates.length >= 2) return coordinates
    }
    return []
  }
  if (candidate.type === 'MultiLineString' && Array.isArray(candidate.coordinates)) {
    return candidate.coordinates.flatMap((line) => readCoordinateArray(line))
  }
  if (candidate.type === 'LineString') return readCoordinateArray(candidate.coordinates)
  return []
}

function readCoordinateArray(value: unknown): [number, number][] {
  if (!Array.isArray(value)) return []
  return value.flatMap((coordinate) => {
    if (!Array.isArray(coordinate) || coordinate.length < 2) return []
    const longitude = Number(coordinate[0])
    const latitude = Number(coordinate[1])
    return Number.isFinite(longitude) &&
      Number.isFinite(latitude) &&
      Math.abs(longitude) <= 180 &&
      Math.abs(latitude) <= 90
      ? ([[longitude, latitude]] as [number, number][])
      : []
  })
}
